import type { GridApiPro } from '@mui/x-data-grid-pro';
import type { GetContractsResponse } from '@vy/api-definition';
import { saveAs } from 'file-saver';
import type { ItemOf } from '@vy/utils';
import { isEmptyArray, isNotNullOrUndefined, vyDate } from '@vy/utils';

import { runMutation } from '../../../../utils/vy-mutation';
import { useNonPagedContractsFromParams } from '../../../../hooks/requests/use-contracts/use-non-paged-contracts-from-params';
import { generateCSV } from '../plans-page-utils/contracts-page-generate-csv';

type UseContractsActionsProps = {
  selectedContracts: ItemOf<GetContractsResponse['contracts']>[];
  apiRef: React.MutableRefObject<GridApiPro>;
};

export const useContractsActions = ({ selectedContracts, apiRef }: UseContractsActionsProps) => {
  const allContractsMutation = useNonPagedContractsFromParams();

  const handleExportSelectedContracts = async () => {
    const shouldExportAll = isEmptyArray(selectedContracts);
    const contractsToExport = await getContractsToExport(shouldExportAll);

    const visibleFields = isNotNullOrUndefined(apiRef?.current)
      ? apiRef.current.getVisibleColumns().map((column) => column.field)
      : undefined;

    const csvContent = generateCSV(contractsToExport ?? [], visibleFields);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });

    const fileName = vyDate().format('MMMM, DD, YYYY');
    saveAs(blob, `${fileName}.csv`);
  };

  const getContractsToExport = async (exportAll: boolean) => {
    if (!exportAll) {
      return selectedContracts;
    }

    const allContractsResponse = await runMutation(allContractsMutation, undefined, {
      pending: 'Downloading all contracts...',
      success: 'All contracts downloaded',
      error: 'Failed to download all contracts',
    });

    return allContractsResponse?.contracts ?? [];
  };

  return {
    handleExportSelectedContracts,
  };
};
